import React, { useEffect, useState } from 'react';

// MUI
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Skeleton,
  Slide,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { send_request } from '../../../utils/Request';
import { DeleteForever } from '@mui/icons-material';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const DeleteButton = styled(Button)({
  backgroundColor: 'rgba(176, 0, 32, 1)',
});

const TopText = styled('span')(({ theme }) => ({
  fontSize: '14px',
  color: theme.palette.text.primary,
}));

const StyledAlert = styled(Alert)(({ theme }) => ({
  border: '1px solid rgba(225, 0, 0, 1)',
  background: 'rgba(225, 0, 0, 0.15)',
  color: theme.palette.text.primary,
  fontSize: '14px',
  lineHeight: '24px',
}));

const Error = styled(DeleteForever)({
  color: 'rgba(225, 0, 0, 1)',
  height: '18px',
  width: 'auto',
  marginTop: '4px',
});

const StyledSkeleton = styled(Skeleton)(({ width, height, radius, theme }) => ({
  borderRadius: radius ? radius : '4px',
  background: theme.palette.action.hover,
  width: width,
  height: height,
  margin: '0px 2px 0px 0px',
  display: 'inline-block',
}));

const ConfirmInput = styled(TextField)({});

function ConfirmDeleteUnsubmittedSessionsDialog({
  isOpen,
  close,
  sessionIds,
  deleteSessions,
  deleteAll = false,
}) {
  const [confirmText, setConfirmText] = useState('');
  const [user, setUser] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const loadUsername = async () => {
      const json = await send_request(`authz-service/user-info`, '', {});
      if (json && json.data) setUser(json.data.displayName);

      return () => {
        setConfirmText('');
      };
    };
    loadUsername();
  }, []);

  const sessionCount = sessionIds?.length || 0;

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={isOpen}
      TransitionComponent={Transition}
      disableEnforceFocus={true}
    >
      <DialogTitle>
        {deleteAll
          ? 'Confirm delete all unsubmitted form sessions'
          : 'Confirm delete unsubmitted form sessions'}
      </DialogTitle>
      <DialogContent>
        <Grid container marginTop="4px" rowGap={2}>
          <Grid item xs={12}>
            <TopText>
              <b>{user ? user : <StyledSkeleton variant="text" width="125px" height="100%" />}</b>{' '}
              is deleting {sessionCount} unsubmitted form session{sessionCount !== 1 ? 's' : ''}
            </TopText>
          </Grid>
          <Grid item xs={12}>
            <StyledAlert severity="error" icon={<Error />}>
              You are about to delete {sessionCount} unsubmitted form session
              {sessionCount !== 1 ? 's' : ''}. This action cannot be undone. The form data saved in
              these sessions will be permanently removed. Are you sure you want to proceed? Type
              "delete" below to confirm.
            </StyledAlert>
          </Grid>
          {sessionCount > 0 && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {sessionCount} session{sessionCount !== 1 ? 's' : ''} selected for deletion
              </Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            <ConfirmInput
              fullWidth
              size="small"
              placeholder='Type "delete" here to confirm'
              onChange={e => setConfirmText(e.target.value)}
              disabled={isDeleting}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={close} disabled={isDeleting}>
          Cancel
        </Button>
        <DeleteButton
          color="error"
          variant="contained"
          onClick={async () => {
            setIsDeleting(true);
            await deleteSessions(sessionIds);
            setIsDeleting(false);
            setConfirmText('');
            close();
          }}
          disabled={isDeleting || confirmText !== 'delete' || sessionCount === 0}
        >
          {isDeleting && <CircularProgress size={24} className="buttonProgress" />}
          Delete {sessionCount} Session{sessionCount !== 1 ? 's' : ''}
        </DeleteButton>
      </DialogActions>
    </Dialog>
  );
}

export default ConfirmDeleteUnsubmittedSessionsDialog;
